import {
  startOfYear,
  endOfYear,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  startOfQuarter,
  endOfQuarter,
  subYears,
  subMonths,
  subWeeks,
  subQuarters,
  parseISO,
  format,
  parse,
  isValid,
  isSameDay,
  getMonth,
} from "date-fns";
import type { PeriodValue } from "@/components/features/revenue/imports/statement-upload/RevenueStatementPeriodPicker";

export const DATE_DISPLAY_FORMAT = "LLL dd, y";

const PARSE_FORMATS = [
  DATE_DISPLAY_FORMAT, // "Jan 01, 2026"
  "LLL d, y",          // "Jan 1, 2026"
  "LLL d yyyy",        // "Jan 1 2026"
  "LLL dd yyyy",       // "Jan 01 2026"
  "MMMM d, y",         // "January 1, 2026"
  "MMMM d yyyy",       // "January 1 2026"
  "MM/dd/yyyy",        // "01/01/2026"
  "M/d/yyyy",          // "1/1/2026"
  "yyyy-MM-dd",        // "2026-01-01"
];

export function parseInputDate(value: string): Date | null {
  const ref = new Date();
  const currentYear = ref.getFullYear();
  const candidates = [value, value.charAt(0).toUpperCase() + value.slice(1)];
  for (const candidate of candidates) {
    for (const fmt of PARSE_FORMATS) {
      const parsed = parse(candidate, fmt, ref);
      if (isValid(parsed)) {
        const year = parsed.getFullYear();
        if (year >= currentYear - 100 && year <= currentYear) return parsed;
      }
    }
  }
  return null;
}
import type { DateRange } from "react-day-picker";
import type { StatementPeriod } from "@/types";

/**
 * Date range preset functions for common date selections
 */
export const dateRangePresets = {
  /**
   * This year: January 1 of current year to today
   */
  thisYear: (): DateRange => {
    const now = new Date();
    return {
      from: startOfYear(now),
      to: now,
    };
  },

  /**
   * This month: First day of current month to today
   */
  thisMonth: (): DateRange => {
    const now = new Date();
    return {
      from: startOfMonth(now),
      to: now,
    };
  },

  /**
   * This week: Monday of current week to today
   */
  thisWeek: (): DateRange => {
    const now = new Date();
    return {
      from: startOfWeek(now, { weekStartsOn: 0 }), // Sunday as start of week
      to: now,
    };
  },

  /**
   * Last year: January 1 to December 31 of previous year
   */
  lastYear: (): DateRange => {
    const lastYear = subYears(new Date(), 1);
    return {
      from: startOfYear(lastYear),
      to: endOfYear(lastYear),
    };
  },

  /**
   * Last month: First to last day of previous month
   */
  lastMonth: (): DateRange => {
    const lastMonth = subMonths(new Date(), 1);
    return {
      from: startOfMonth(lastMonth),
      to: endOfMonth(lastMonth),
    };
  },

  /**
   * Last week: Monday to Sunday of previous week
   */
  lastWeek: (): DateRange => {
    const lastWeek = subWeeks(new Date(), 1);
    return {
      from: startOfWeek(lastWeek, { weekStartsOn: 0 }), // Sunday as start of week
      to: endOfWeek(lastWeek, { weekStartsOn: 0 }),
    };
  },

  /**
   * Last quarter: First to last day of previous quarter
   */
  lastQuarter: (): DateRange => {
    const lastQuarter = subQuarters(new Date(), 1);
    return {
      from: startOfQuarter(lastQuarter),
      to: endOfQuarter(lastQuarter),
    };
  },

  /**
   * Last 6 months: First day of 6 months ago to last day of previous month
   */
  lastSixMonths: (): DateRange => {
    const sixMonthsAgo = subMonths(new Date(), 6);
    const lastMonth = subMonths(new Date(), 1);
    return {
      from: startOfMonth(sixMonthsAgo),
      to: endOfMonth(lastMonth),
    };
  },

  /**
   * Last half year: Jan 1 - Jun 30 of current year (if in H2), or Jul 1 - Dec 31 of previous year (if in H1)
   */
  lastHalfYear: (): DateRange => {
    const now = new Date();
    const month = getMonth(now); // 0-indexed: 0=Jan, 6=Jul
    if (month >= 6) {
      // H2 (Jul-Dec): last half year is H1 of current year
      const year = now.getFullYear();
      return {
        from: new Date(year, 0, 1),  // Jan 1
        to: new Date(year, 5, 30),   // Jun 30
      };
    } else {
      // H1 (Jan-Jun): last half year is H2 of previous year
      const year = now.getFullYear() - 1;
      return {
        from: new Date(year, 6, 1),   // Jul 1
        to: new Date(year, 11, 31),   // Dec 31
      };
    }
  },

  /**
   * All time: Returns undefined to clear the date range
   */
  allTime: (): DateRange | undefined => {
    return undefined;
  },
};

export type StatementPeriodPresetKey = Exclude<keyof typeof dateRangePresets, "allTime">;

export const STATEMENT_PERIOD_PRESETS: { label: string; preset: StatementPeriodPresetKey }[] = [
  { label: "Last month", preset: "lastMonth" },
  { label: "Last quarter", preset: "lastQuarter" },
  { label: "Last half year", preset: "lastHalfYear" },
  { label: "Last year", preset: "lastYear" },
];

export const statementPeriodToPreset: Record<StatementPeriod, keyof typeof dateRangePresets> = {
  monthly: "lastMonth",
  quarterly: "lastQuarter",
  half_yearly: "lastHalfYear",
  annually: "lastYear",
};

/**
 * Months of the year, indexed 0 (January) - 11 (December), for period pickers.
 */
export const MONTHS: { label: string; value: number }[] = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
].map((label, value) => ({ label, value }));

/**
 * Calendar quarters, numbered 1 - 4, for period pickers.
 */
export const QUARTERS: { label: string; value: number }[] = [
  { label: "Q1 - Jan-Mar", value: 1 },
  { label: "Q2 - Apr-Jun", value: 2 },
  { label: "Q3 - Jul-Sep", value: 3 },
  { label: "Q4 - Oct-Dec", value: 4 },
];

/**
 * The most recent `count` years, newest first (e.g. 2026, 2025, ... ).
 */
export const getRecentYears = (count: number): number[] => {
  const currentYear = new Date().getFullYear();
  return Array.from({ length: count }, (_, i) => currentYear - i);
};

/**
 * The calendar quarter (1-4) containing the given month (month is 0-indexed).
 */
export const monthToQuarter = (month: number): number => Math.floor(month / 3) + 1;

/**
 * Calendar halves, numbered 1 - 2, for period pickers.
 */
export const HALVES: { label: string; value: number }[] = [
  { label: "H1 - Jan-Jun", value: 1 },
  { label: "H2 - Jul-Dec", value: 2 },
];

/**
 * The calendar half (1-2) containing the given month (month is 0-indexed).
 */
export const monthToHalf = (month: number): number => (month < 6 ? 1 : 2);

/**
 * First and last day of the given month (month is 0-indexed).
 */
export const monthToDateRange = (year: number, month: number): { start: Date; end: Date } => {
  const start = startOfMonth(new Date(year, month, 1));
  return { start, end: endOfMonth(start) };
};

/**
 * First and last day of the given quarter (quarter is 1-4).
 */
export const quarterToDateRange = (year: number, quarter: number): { start: Date; end: Date } => {
  const firstMonth = (quarter - 1) * 3;
  const start = startOfMonth(new Date(year, firstMonth, 1));
  return { start, end: endOfMonth(new Date(year, firstMonth + 2, 1)) };
};

/**
 * First and last day of the period a picker value describes, driven by the
 * value's periodType.
 */
export const periodValueToDateRange = (period: PeriodValue): { start: Date; end: Date } => {
  const { periodType, month, quarter, year } = period;
  return periodType === "monthly" ? monthToDateRange(year, month) : quarterToDateRange(year, quarter);
};

/**
 * Inverse of periodValueToDateRange: recover a period picker value from a
 * stored period. Returns null when the range is not an exact calendar month
 * or quarter (e.g. legacy statements whose period was aggregated from line
 * dates).
 */
export const dateRangeToPeriodValue = (
  start: string | null | undefined,
  end: string | null | undefined,
): PeriodValue | null => {
  if (!start || !end) return null;
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  if (!isValid(startDate) || !isValid(endDate)) return null;

  const year = startDate.getFullYear();
  const month = startDate.getMonth();
  const quarter = monthToQuarter(month);

  const monthRange = monthToDateRange(year, month);
  if (isSameDay(startDate, monthRange.start) && isSameDay(endDate, monthRange.end)) {
    return { periodType: "monthly", month, quarter, year };
  }

  const quarterRange = quarterToDateRange(year, quarter);
  if (isSameDay(startDate, quarterRange.start) && isSameDay(endDate, quarterRange.end)) {
    return { periodType: "quarterly", month: (quarter - 1) * 3, quarter, year };
  }

  return null;
};

/**
 * Statement periods — the cadence a royalty run is settled on.
 *
 * Royalty statements are generated for a whole calendar period, so the picker
 * works in period units (Q1 2026) rather than two loose dates. `StatementPeriodValue`
 * carries all four unit fields at once so switching the type keeps the user's
 * other choices intact.
 */
export type StatementPeriodType = "monthly" | "quarterly" | "half_yearly" | "yearly";

export interface StatementPeriodValue {
  periodType: StatementPeriodType;
  /** 0-indexed, as in Date#getMonth. */
  month: number;
  /** 1 - 4. */
  quarter: number;
  /** 1 - 2. */
  half: number;
  year: number;
}

export const STATEMENT_PERIOD_TYPES: { label: string; value: StatementPeriodType }[] = [
  { label: "Monthly", value: "monthly" },
  { label: "Quarterly", value: "quarterly" },
  { label: "Half-yearly", value: "half_yearly" },
  { label: "Yearly", value: "yearly" },
];

/** First and last day of the given half (half is 1-2). */
export const halfYearToDateRange = (year: number, half: number): { start: Date; end: Date } => {
  const firstMonth = (half - 1) * 6;
  const start = startOfMonth(new Date(year, firstMonth, 1));
  return { start, end: endOfMonth(new Date(year, firstMonth + 5, 1)) };
};

/** First and last day of the given year. */
export const yearToDateRange = (year: number): { start: Date; end: Date } => {
  const start = startOfYear(new Date(year, 0, 1));
  return { start, end: endOfYear(start) };
};

/** First and last day of the period a statement picker value describes. */
export const statementPeriodValueToDateRange = (
  period: StatementPeriodValue,
): { start: Date; end: Date } => {
  const { periodType, month, quarter, half, year } = period;
  if (periodType === "monthly") return monthToDateRange(year, month);
  if (periodType === "quarterly") return quarterToDateRange(year, quarter);
  if (periodType === "half_yearly") return halfYearToDateRange(year, half);
  return yearToDateRange(year);
};

/** A picker value covering the given date, at the given cadence. */
export const statementPeriodValueForDate = (
  date: Date,
  periodType: StatementPeriodType,
): StatementPeriodValue => {
  const month = date.getMonth();
  return {
    periodType,
    month,
    quarter: monthToQuarter(month),
    half: monthToHalf(month),
    year: date.getFullYear(),
  };
};

/**
 * Inverse of statementPeriodValueToDateRange: recover a picker value from a
 * stored period. Returns null when the range is not an exact calendar period —
 * a custom range the picker can't express.
 */
export const dateRangeToStatementPeriodValue = (
  start: string | null | undefined,
  end: string | null | undefined,
): StatementPeriodValue | null => {
  if (!start || !end) return null;
  const startDate = parseISO(start);
  const endDate = parseISO(end);
  if (!isValid(startDate) || !isValid(endDate)) return null;

  const base = statementPeriodValueForDate(startDate, "monthly");
  const matches = (periodType: StatementPeriodType) => {
    const range = statementPeriodValueToDateRange({ ...base, periodType });
    return isSameDay(startDate, range.start) && isSameDay(endDate, range.end);
  };

  // Widest first: Jan 1 - Dec 31 is a year, not four quarters.
  for (const periodType of ["yearly", "half_yearly", "quarterly", "monthly"] as const) {
    if (matches(periodType)) return { ...base, periodType };
  }
  return null;
};

/** Short label for a period value, e.g. "Q1 2026" / "H1 2026" / "March 2026". */
export const formatStatementPeriodLabel = (period: StatementPeriodValue): string => {
  const { periodType, month, quarter, half, year } = period;
  if (periodType === "monthly") return `${MONTHS[month].label} ${year}`;
  if (periodType === "quarterly") return `Q${quarter} ${year}`;
  if (periodType === "half_yearly") return `H${half} ${year}`;
  return String(year);
};

/** The picker cadence matching a contract's statement period. */
export const statementPeriodToPeriodType: Record<StatementPeriod, StatementPeriodType> = {
  monthly: "monthly",
  quarterly: "quarterly",
  half_yearly: "half_yearly",
  annually: "yearly",
};

/**
 * Get a date range preset by name
 */
export const getDateRangePreset = (
  preset: keyof typeof dateRangePresets,
): DateRange | undefined => {
  return dateRangePresets[preset]();
};

export const dateRangeButtonsThisYear = [
  {
    label: "This year",
    dateRangePreset: dateRangePresets.thisYear(),
  },
  {
    label: "This month",
    dateRangePreset: dateRangePresets.thisMonth(),
  },
  {
    label: "This week",
    dateRangePreset: dateRangePresets.thisWeek(),
  },
];

export const dateRangeButtonsLastYear = [
  {
    label: "Last year",
    dateRangePreset: dateRangePresets.lastYear(),
  },
  {
    label: "Last month",
    dateRangePreset: dateRangePresets.lastMonth(),
  },
  {
    label: "Last week",
    dateRangePreset: dateRangePresets.lastWeek(),
  },
];

export const DEFAULT_DATE_RANGE_PRESETS: {
  label: string;
  dateRangePreset: DateRange | undefined;
  divider?: boolean;
}[] = [
  ...dateRangeButtonsThisYear,
  { ...dateRangeButtonsLastYear[0], divider: true },
  ...dateRangeButtonsLastYear.slice(1),
  { label: "All time", dateRangePreset: dateRangePresets.allTime(), divider: true },
];

/**
 * Format a date-only string (YYYY-MM-DD) to a localized date string
 * without timezone conversion issues.
 *
 * When the API returns a date-only string like "2025-01-15", we don't want
 * to interpret it as UTC midnight and then convert to local time, which
 * would shift the date by one day in timezones behind UTC.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @param locale - Optional locale string (defaults to browser locale)
 * @returns Formatted date string (e.g., "1/15/2025" in en-US)
 */
export function formatDateOnly(dateString: string | null | undefined, locale?: string): string {
  if (!dateString) return "";
  return parseISO(dateString).toLocaleDateString(locale);
}

/**
 * Format a date-only string (YYYY-MM-DD) to a readable date format.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted date string (e.g., "Mar 15, 2025")
 */
export function formatReadableDate(dateString: string | null | undefined): string {
  if (!dateString) return "";
  return format(parseISO(dateString), "MMM d, yyyy");
}

/**
 * Format a date-only string (YYYY-MM-DD) as month and year.
 *
 * @param dateString - Date string in YYYY-MM-DD format
 * @returns Formatted month-year string (e.g., "Apr 2026")
 */
export function formatMonthYear(dateString: string | null | undefined): string {
  if (!dateString) return "";
  return format(parseISO(dateString), "MMM yyyy");
}

/**
 * Format a date period from start and end dates.
 *
 * @param startDate - Start date string in YYYY-MM-DD format
 * @param endDate - End date string in YYYY-MM-DD format
 * @returns Formatted date period string (e.g., "Jan 1, 2024 - Jun 30, 2024")
 */
export function formatDatePeriod(
  startDate: string | null | undefined,
  endDate: string | null | undefined,
): string {
  if (!startDate || !endDate) return "";
  return `${formatReadableDate(startDate)} - ${formatReadableDate(endDate)}`;
}

/**
 * Format an ISO datetime string to a readable date and time format.
 *
 * @param dateTimeString - ISO datetime string (e.g., "2025-12-02T10:01:00Z")
 * @returns Formatted datetime string (e.g., "Dec 2, 2025 10:01")
 */
export function formatDateTime(dateTimeString: string | null | undefined): string {
  if (!dateTimeString) return "";
  return format(parseISO(dateTimeString), "MMM d, yyyy HH:mm");
}
